import styled from "@emotion/styled/macro";
import { keyframes } from "@emotion/react";

/* ════════════════════════════════════════════════════════════
   ANC Clinical Assessment — Enhanced Styled Components
   Clean, clinical-grade UI with excellent responsive design
   Color theme: Medical blue (#3871B7) with teal accents
   ════════════════════════════════════════════════════════════ */

const PRIMARY = "#3871B7";
const PRIMARY_LIGHT = "#5a8fd4";
const PRIMARY_BG = "#ebf0f9";
const TEXT_DARK = "#1A1818";
const TEXT_MID = "#484449";
const TEXT_MUTED = "#6f7077";
const BORDER = "#e4e5e9";
const BG_PAGE = "#f5f7fa";
const BG_CARD = "#fff";
const SUCCESS = "#2e7d32";
const SUCCESS_BG = "#e8f5e9";
const WARNING = "#e65100";
const WARNING_BG = "#fff3e0";
const DANGER = "#c62828";
const DANGER_BG = "#ffebee";

/* ─── Animations ─── */
const slideIn = keyframes`
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
`;

/* ─── Page Layout ──────────────────────────────────────────── */

export const AssessmentContainer = styled("div")({
    padding: "0 16px 30px",
    background: BG_PAGE,
    minHeight: "calc(100vh - 120px)",
});

/* ─── Pregnancy Progress Header ────────────────────────────── */

export const PregnancyHeader = styled("div")({
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    borderRadius: "12px",
    padding: "18px 22px",
    color: "#fff",
    marginBottom: "16px",
    boxShadow: "0 4px 16px rgba(56,113,183,0.2)",
    "@media (max-width: 576px)": {
        padding: "14px 16px",
        borderRadius: "10px",
    },
});

export const PregnancyHeaderTop = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px",
    flexWrap: "wrap",
    gap: "8px",
});

export const PregnancyTitle = styled("h2")({
    margin: 0,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "8px",
});

export const PregnancySubtitle = styled("span")({
    fontSize: "0.78rem",
    fontWeight: 400,
    opacity: 0.85,
    display: "block",
    marginTop: "2px",
});

export const PregnancyStatusChip = styled("span")(function(props) {
    return {
        padding: "4px 14px",
        borderRadius: "16px",
        fontSize: "0.75rem",
        fontWeight: 600,
        background: props.variant === "danger" ? "rgba(198,40,40,0.25)" :
                    props.variant === "warning" ? "rgba(255,152,0,0.25)" :
                    "rgba(255,255,255,0.2)",
        color: "#fff",
        backdropFilter: "blur(4px)",
    };
});

export const PregnancyStatsRow = styled("div")({
    display: "flex",
    gap: "0",
    flexWrap: "wrap",
    marginBottom: "14px",
    "@media (max-width: 768px)": {
        gap: "6px",
    },
});

export const PregnancyStat = styled("div")(function(props) {
    return {
        flex: 1,
        minWidth: "100px",
        padding: "0 16px",
        borderRight: props.last ? "none" : "1px solid rgba(255,255,255,0.2)",
        "@media (max-width: 768px)": {
            minWidth: "80px",
            padding: "4px 10px",
            flex: "0 0 auto",
            borderRight: "none",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "8px",
        },
    };
});

export const PregnancyStatLabel = styled("div")({
    fontSize: "0.65rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    opacity: 0.7,
});

export const PregnancyStatValue = styled("div")(function(props) {
    return {
        fontSize: props.large ? "1.3rem" : "0.95rem",
        fontWeight: 700,
        lineHeight: 1.2,
    };
});

/* ─── Progress Bar ─── */
export const ProgressBarWrapper = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "12px",
});

export const ProgressBarTrack = styled("div")({
    flex: 1,
    height: "8px",
    borderRadius: "4px",
    background: "rgba(255,255,255,0.2)",
    overflow: "hidden",
});

export const ProgressBarFill = styled("div")(function(props) {
    return {
        height: "100%",
        borderRadius: "4px",
        background: props.percent > 90 ? "#ff9800" :
                    props.percent > 70 ? "#e8f5e9" : "#fff",
        width: (props.percent || 0) + "%",
        transition: "width 0.6s ease",
    };
});

export const ProgressLabel = styled("span")({
    fontSize: "0.72rem",
    fontWeight: 600,
    opacity: 0.9,
    whiteSpace: "nowrap",
});

export const TrimesterMarkers = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    marginTop: "4px",
    position: "relative",
});

export const TrimesterMark = styled("span")(function(props) {
    return {
        fontSize: "0.6rem",
        fontWeight: props.active ? 700 : 400,
        opacity: props.active ? 1 : 0.5,
    };
});

/* ─── View Toggle Tabs ─────────────────────────────────────── */

export const ViewToggle = styled("div")({
    display: "flex",
    gap: "0",
    marginBottom: "16px",
    borderRadius: "10px",
    overflow: "hidden",
    border: "1px solid " + BORDER,
    width: "fit-content",
    background: BG_CARD,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
});

export const ViewToggleBtn = styled("button")(function(props) {
    return {
        padding: "10px 22px",
        fontSize: "0.82rem",
        fontWeight: props.active ? 600 : 400,
        color: props.active ? "#fff" : TEXT_MID,
        background: props.active ? PRIMARY : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        position: "relative",
        ":not(:last-child)": {
            borderRight: "1px solid " + BORDER,
        },
        ":hover": {
            background: props.active ? PRIMARY : "#f5f7fa",
        },
    };
});

export const ContactCountBadge = styled("span")(function(props) {
    return {
        fontSize: "0.68rem",
        fontWeight: 700,
        background: props.active ? "rgba(255,255,255,0.25)" : PRIMARY_BG,
        color: props.active ? "#fff" : PRIMARY,
        padding: "2px 8px",
        borderRadius: "10px",
        minWidth: "20px",
        textAlign: "center",
    };
});

/* ─── Form Completion Progress ──────────────────────────────── */

export const FormProgressBar = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 16px",
    background: BG_CARD,
    border: "1px solid " + BORDER,
    borderRadius: "10px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    flexWrap: "wrap",
});

export const FormProgressLabel = styled("span")({
    fontSize: "0.78rem",
    fontWeight: 600,
    color: TEXT_MID,
    whiteSpace: "nowrap",
});

export const FormProgressTrack = styled("div")({
    flex: 1,
    height: "6px",
    borderRadius: "3px",
    background: "#f0f0f0",
    overflow: "hidden",
    minWidth: "120px",
});

export const FormProgressFill = styled("div")(function(props) {
    return {
        height: "100%",
        borderRadius: "3px",
        background: props.percent >= 80 ? SUCCESS : props.percent >= 40 ? "#f9a825" : "#e0e0e0",
        width: (props.percent || 0) + "%",
        transition: "width 0.4s ease",
    };
});

export const FormProgressPercent = styled("span")(function(props) {
    return {
        fontSize: "0.78rem",
        fontWeight: 700,
        color: props.percent >= 80 ? SUCCESS : props.percent >= 40 ? "#f57f17" : TEXT_MUTED,
        minWidth: "36px",
    };
});

/* ─── Section Quick Nav Pills ──────────────────────────────── */

export const SectionNavRow = styled("div")({
    display: "flex",
    gap: "6px",
    marginBottom: "14px",
    flexWrap: "wrap",
});

export const SectionNavPill = styled("button")(function(props) {
    const filled = props.filled;
    const active = props.active;
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.75rem",
        fontWeight: active ? 700 : 500,
        background: active ? PRIMARY : filled ? SUCCESS_BG : "#f5f5f5",
        color: active ? "#fff" : filled ? SUCCESS : TEXT_MUTED,
        border: "1px solid " + (active ? PRIMARY : filled ? SUCCESS + "40" : BORDER),
        cursor: "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        alignItems: "center",
        gap: "5px",
        ":hover": {
            background: active ? PRIMARY : filled ? SUCCESS_BG : "#eee",
            transform: "translateY(-1px)",
        },
    };
});

/* ─── Info Banner (Gestational Age, Booking Summary) ───────── */

export const InfoBanner = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    background: BG_CARD,
    border: "1px solid " + BORDER,
    borderLeft: "4px solid " + PRIMARY,
    borderRadius: "8px",
    marginBottom: "16px",
    flexWrap: "wrap",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    "@media (max-width: 768px)": {
        flexDirection: "column",
        alignItems: "flex-start",
    },
});

export const InfoBannerItem = styled("div")(function(props) {
    return {
        display: "flex",
        flexDirection: "column",
        padding: "0 16px",
        borderRight: "1px solid " + BORDER,
        ":last-child": {
            borderRight: "none",
        },
        "@media (max-width: 768px)": {
            borderRight: "none",
            padding: "4px 0",
        },
    };
});

export const InfoLabel = styled("span")({
    fontSize: "0.6rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: TEXT_MUTED,
});

export const InfoValue = styled("span")(function(props) {
    return {
        fontSize: props.size === "large" ? "1.15rem" : "0.88rem",
        fontWeight: props.size === "large" ? 700 : 600,
        color: props.color || TEXT_DARK,
        lineHeight: 1.3,
    };
});

/* ─── Danger Sign Alerts ───────────────────────────────────── */

export const AlertsContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
});

export const AlertCard = styled("div")(function(props) {
    const type = props.type || "warning";
    const typeMap = {
        critical: { bg: "#fff5f5", border: "#fc8181", iconBg: "#fed7d7", stripe: DANGER },
        warning: { bg: "#fffff0", border: "#f6e05e", iconBg: "#fefcbf", stripe: "#e69500" },
        info: { bg: "#ebf8ff", border: "#63b3ed", iconBg: "#bee3f8", stripe: "#2b6cb0" },
    };
    const t = typeMap[type] || typeMap.warning;
    return {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 16px",
        background: t.bg,
        border: "1px solid " + t.border,
        borderRadius: "10px",
        borderLeft: "4px solid " + t.stripe,
        animation: slideIn + " 0.3s ease",
        boxShadow: "0 2px 8px " + t.border + "30",
    };
});

export const AlertIcon = styled("span")({
    fontSize: "20px",
    flexShrink: 0,
    lineHeight: 1,
});

export const AlertContent = styled("div")({
    flex: 1,
});

export const AlertTitle = styled("div")(function(props) {
    const type = props.type || "warning";
    return {
        fontSize: "0.82rem",
        fontWeight: 700,
        color: type === "critical" ? "#c53030" : type === "warning" ? "#b7791f" : "#2b6cb0",
        marginBottom: "2px",
    };
});

export const AlertDetail = styled("div")({
    fontSize: "0.78rem",
    color: TEXT_MID,
    lineHeight: 1.4,
});

export const AlertAction = styled("div")({
    fontSize: "0.72rem",
    color: TEXT_MUTED,
    fontStyle: "italic",
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
});

/* ─── Form Card / Section ──────────────────────────────────── */

export const FormCard = styled("div")(function(props) {
    return {
        background: BG_CARD,
        border: "1px solid " + BORDER,
        borderRadius: "10px",
        marginBottom: "14px",
        overflow: "hidden",
        borderTop: "3px solid " + (props.borderColor || PRIMARY),
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.2s ease",
        ":hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        },
    };
});

export const FormCardHeader = styled("div")(function(props) {
    return {
        display: "flex",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: props.collapsed ? "none" : "1px solid " + BORDER,
        cursor: "pointer",
        userSelect: "none",
        transition: "background 0.15s",
        ":hover": {
            background: "rgba(56,113,183,0.03)",
        },
    };
});

export const FormCardIcon = styled("span")(function(props) {
    return {
        width: "30px",
        height: "30px",
        borderRadius: "8px",
        background: (props.color || PRIMARY) + "12",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "15px",
        flexShrink: 0,
        marginRight: "10px",
    };
});

export const FormCardTitle = styled("h6")(function(props) {
    return {
        fontSize: "0.82rem",
        fontWeight: 700,
        color: props.color || PRIMARY,
        margin: 0,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        flex: 1,
    };
});

export const FormCardBadge = styled("span")(function(props) {
    const variant = props.variant || "default";
    const variants = {
        filled: { bg: SUCCESS_BG, color: SUCCESS },
        partial: { bg: "#fff8e1", color: "#f57f17" },
        empty: { bg: "#f5f5f5", color: TEXT_MUTED },
        "default": { bg: "#f5f5f5", color: TEXT_MUTED },
    };
    const v = variants[variant] || variants["default"];
    return {
        fontSize: "0.68rem",
        fontWeight: 600,
        borderRadius: "12px",
        padding: "3px 10px",
        background: v.bg,
        color: v.color,
    };
});

export const CollapseIcon = styled("span")(function(props) {
    return {
        display: "inline-flex",
        transition: "transform 0.25s ease",
        transform: props.collapsed ? "rotate(-90deg)" : "rotate(0deg)",
        fontSize: "14px",
        color: TEXT_MUTED,
        marginLeft: "8px",
    };
});

export const FormCardBody = styled("div")(function(props) {
    return {
        padding: props.collapsed ? "0 16px" : "16px",
        maxHeight: props.collapsed ? "0" : "2000px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        opacity: props.collapsed ? 0 : 1,
    };
});

/* ─── Form Grid ────────────────────────────────────────────── */

export const FormGrid = styled("div")(function(props) {
    const cols = props.columns || 4;
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + cols + ", 1fr)",
        gap: "12px 16px",
        "@media (max-width: 1200px)": {
            gridTemplateColumns: cols > 2 ? "repeat(3, 1fr)" : "repeat(" + cols + ", 1fr)",
        },
        "@media (max-width: 992px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        "@media (max-width: 576px)": {
            gridTemplateColumns: "1fr",
        },
    };
});

export const FormGroup = styled("div")(function(props) {
    return {
        gridColumn: "span " + (props.span || 1),
        "@media (max-width: 576px)": {
            gridColumn: "span 1",
        },
    };
});

export const FormLabel = styled("label")(function(props) {
    return {
        display: "block",
        fontSize: "0.78rem",
        fontWeight: 500,
        color: TEXT_MID,
        marginBottom: "4px",
        "::after": props.required
            ? { content: '"*"', color: DANGER, marginLeft: "3px" }
            : {},
    };
});

/* ─── Inline Computed Values ───────────────────────────────── */

export const ComputedBadge = styled("span")(function(props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "16px",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: props.color || PRIMARY,
        background: props.bg || (props.color || PRIMARY) + "15",
        marginTop: "5px",
    };
});

export const ComputedRow = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "4px",
});

/* ─── Blood Pressure Dual Input ────────────────────────────── */

export const BPInputGroup = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "& input": {
        width: "70px",
        textAlign: "center",
    },
    "@media (max-width: 576px)": {
        "& input": {
            width: "60px",
        },
    },
});

export const BPSeparator = styled("span")({
    fontSize: "18px",
    fontWeight: 700,
    color: TEXT_MUTED,
    lineHeight: 1,
});

/* ─── MUAC Visual Indicator ────────────────────────────────── */

export const MUACIndicator = styled("div")(function(props) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 12px",
        borderRadius: "8px",
        background: props.muacBg || SUCCESS_BG,
        border: "1px solid " + (props.muacColor || SUCCESS) + "30",
        marginTop: "6px",
    };
});

export const MUACDot = styled("span")(function(props) {
    return {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: props.color || SUCCESS,
        flexShrink: 0,
        boxShadow: "0 0 0 3px " + (props.color || SUCCESS) + "30",
    };
});

export const MUACLabel = styled("span")(function(props) {
    return {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: props.color || SUCCESS,
    };
});

/* ─── Weight Trend Indicator ───────────────────────────────── */

export const WeightTrend = styled("div")(function(props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "0.72rem",
        fontWeight: 600,
        color: props.color || TEXT_MUTED,
        marginTop: "4px",
    };
});

/* ─── Submit / Action Buttons ──────────────────────────────── */

export const FormActions = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px 0 0",
    borderTop: "1px solid " + BORDER,
    marginTop: "8px",
    flexWrap: "wrap",
    "@media (max-width: 576px)": {
        justifyContent: "stretch",
        "& button": {
            flex: 1,
        },
    },
});

export const PrimaryButton = styled("button")(function(props) {
    return {
        padding: "10px 28px",
        background: props.disabled ? "#bbb" : PRIMARY,
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        fontSize: "0.82rem",
        fontWeight: 600,
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        boxShadow: props.disabled ? "none" : "0 2px 8px " + PRIMARY + "30",
        ":hover": {
            opacity: props.disabled ? 1 : 0.9,
            transform: props.disabled ? "none" : "translateY(-1px)",
            boxShadow: props.disabled ? "none" : "0 4px 12px " + PRIMARY + "40",
        },
    };
});

export const SecondaryButton = styled("button")({
    padding: "10px 20px",
    background: BG_CARD,
    color: TEXT_MID,
    border: "1px solid " + BORDER,
    borderRadius: "8px",
    fontSize: "0.82rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    ":hover": {
        background: "#f5f7fa",
        borderColor: "#ccc",
    },
});

/* ─── Timeline Styles ──────────────────────────────────────── */

export const TimelineContainer = styled("div")({
    position: "relative",
    padding: "0 0 0 28px",
    "::before": {
        content: '""',
        position: "absolute",
        left: "10px",
        top: "12px",
        bottom: "12px",
        width: "2px",
        background: "linear-gradient(to bottom, " + PRIMARY + ", " + BORDER + ")",
        borderRadius: "1px",
    },
});

export const TimelineItem = styled("div")(function(props) {
    return {
        position: "relative",
        padding: "14px 18px",
        marginBottom: "10px",
        background: props.active ? PRIMARY_BG : BG_CARD,
        border: "1px solid " + (props.active ? PRIMARY + "40" : BORDER),
        borderRadius: "10px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        ":hover": {
            borderColor: PRIMARY,
            boxShadow: "0 3px 12px rgba(56,113,183,0.1)",
            transform: "translateX(2px)",
        },
        "::before": {
            content: '""',
            position: "absolute",
            left: "-23px",
            top: "20px",
            width: "12px",
            height: "12px",
            borderRadius: "50%",
            background: props.active ? PRIMARY : BG_CARD,
            border: "3px solid " + (props.active ? PRIMARY : "#bbb"),
            boxShadow: props.active ? "0 0 0 4px " + PRIMARY + "25" : "none",
        },
    };
});

export const TimelineHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px",
    flexWrap: "wrap",
    gap: "6px",
});

export const TimelineContact = styled("span")({
    fontSize: "0.88rem",
    fontWeight: 700,
    color: PRIMARY,
});

export const TimelineDate = styled("span")({
    fontSize: "0.72rem",
    color: TEXT_MUTED,
});

export const TimelineGA = styled("span")(function(props) {
    return {
        fontSize: "0.72rem",
        fontWeight: 600,
        color: props.color || PRIMARY,
        background: (props.color || PRIMARY) + "12",
        padding: "3px 10px",
        borderRadius: "12px",
    };
});

export const TimelineGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "4px 12px",
    "@media (max-width: 768px)": {
        gridTemplateColumns: "repeat(2, 1fr)",
    },
});

export const TimelineStat = styled("div")({
    display: "flex",
    flexDirection: "column",
    padding: "4px 0",
});

export const TimelineStatLabel = styled("span")({
    fontSize: "0.6rem",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: TEXT_MUTED,
    fontWeight: 600,
});

export const TimelineStatValue = styled("span")(function(props) {
    return {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: props.danger ? DANGER : TEXT_DARK,
    };
});

/* ─── Timeline Trend Indicators ────────────────────────────── */

export const TrendBadge = styled("span")(function(props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: props.direction === "up" ? SUCCESS : props.direction === "down" ? WARNING : TEXT_MUTED,
        marginLeft: "4px",
    };
});

/* ─── Empty State ──────────────────────────────────────────── */

export const EmptyState = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "50px 20px",
    textAlign: "center",
});

export const EmptyIcon = styled("div")({
    fontSize: "52px",
    marginBottom: "14px",
    opacity: 0.5,
});

export const EmptyTitle = styled("h6")({
    fontSize: "1rem",
    fontWeight: 600,
    color: TEXT_DARK,
    margin: "0 0 6px 0",
});

export const EmptyDescription = styled("p")({
    fontSize: "0.82rem",
    color: TEXT_MUTED,
    margin: 0,
    maxWidth: "420px",
    lineHeight: 1.6,
});

/* ─── Contact Detail View ──────────────────────────────────── */

export const DetailCard = styled("div")({
    background: BG_CARD,
    border: "1px solid " + BORDER,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    animation: slideIn + " 0.3s ease",
});

export const DetailHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid " + BORDER,
    background: PRIMARY_BG,
    flexWrap: "wrap",
    gap: "8px",
});

export const DetailTitle = styled("h5")({
    fontSize: "1rem",
    fontWeight: 700,
    color: PRIMARY,
    margin: 0,
});

export const DetailBody = styled("div")({
    padding: "20px",
});

export const DetailSection = styled("div")({
    marginBottom: "22px",
    ":last-child": {
        marginBottom: 0,
    },
});

export const DetailSectionTitle = styled("h6")({
    fontSize: "0.78rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: PRIMARY,
    marginBottom: "12px",
    paddingBottom: "6px",
    borderBottom: "2px solid " + PRIMARY_BG,
    display: "flex",
    alignItems: "center",
    gap: "8px",
});

export const DetailGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px 16px",
    "@media (max-width: 992px)": {
        gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (max-width: 768px)": {
        gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (max-width: 480px)": {
        gridTemplateColumns: "1fr",
    },
});

export const DetailItem = styled("div")({
    display: "flex",
    flexDirection: "column",
    padding: "8px 12px",
    background: "#fafbfc",
    borderRadius: "8px",
    border: "1px solid #f0f0f0",
});

export const DetailItemLabel = styled("span")({
    fontSize: "0.65rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: TEXT_MUTED,
});

export const DetailItemValue = styled("span")(function(props) {
    return {
        fontSize: "0.9rem",
        fontWeight: 600,
        color: props.danger ? DANGER : TEXT_DARK,
        marginTop: "2px",
    };
});

/* ─── Clinical Notes Box ───────────────────────────────────── */

export const ClinicalNotesBox = styled("div")({
    padding: "12px 16px",
    background: "#fafbfc",
    borderRadius: "8px",
    border: "1px solid #f0f0f0",
    marginTop: "4px",
});

export const ClinicalNotesLabel = styled("div")({
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    color: TEXT_MUTED,
    marginBottom: "6px",
});

export const ClinicalNotesText = styled("p")({
    fontSize: "0.85rem",
    color: TEXT_MID,
    lineHeight: 1.6,
    margin: 0,
    whiteSpace: "pre-wrap",
});

/* ─── No Booking CTA ───────────────────────────────────────── */

export const NoBookingCard = styled("div")({
    background: BG_CARD,
    border: "1px solid " + BORDER,
    borderRadius: "12px",
    padding: "40px 30px",
    textAlign: "center",
    borderTop: "3px solid #f6ad55",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
});

export const NoBookingIcon = styled("div")({
    fontSize: "56px",
    marginBottom: "14px",
});

export const NoBookingTitle = styled("h5")({
    fontSize: "1.1rem",
    fontWeight: 700,
    color: TEXT_DARK,
    margin: "0 0 10px 0",
});

export const NoBookingDesc = styled("p")({
    fontSize: "0.88rem",
    color: TEXT_MUTED,
    margin: "0 0 24px 0",
    maxWidth: "500px",
    marginLeft: "auto",
    marginRight: "auto",
    lineHeight: 1.6,
});

export const NoBookingButton = styled("button")({
    padding: "12px 28px",
    background: PRIMARY,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px " + PRIMARY + "30",
    ":hover": {
        opacity: 0.9,
        transform: "translateY(-1px)",
        boxShadow: "0 4px 16px " + PRIMARY + "40",
    },
});

/* ─── Seen By Badge ────────────────────────────────────────── */

export const SeenByBadge = styled("div")({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 12px",
    background: PRIMARY_BG,
    borderRadius: "16px",
    fontSize: "0.72rem",
    fontWeight: 500,
    color: PRIMARY,
    marginTop: "8px",
});

/* ─── Loading Skeleton ─────────────────────────────────────── */

export const SkeletonLine = styled("div")(function(props) {
    return {
        width: props.width || "100%",
        height: props.height || "14px",
        borderRadius: "6px",
        background: "linear-gradient(90deg, #e4e5e9 25%, #f1f1f4 50%, #e4e5e9 75%)",
        backgroundSize: "200% 100%",
        animation: shimmer + " 1.5s infinite",
    };
});

/* ─── Responsive Helper ────────────────────────────────────── */

export const MobileOnly = styled("div")({
    display: "none",
    "@media (max-width: 768px)": {
        display: "block",
    },
});

export const DesktopOnly = styled("div")({
    display: "block",
    "@media (max-width: 768px)": {
        display: "none",
    },
});
